/**
 * Single source of truth for which paths get their own prerendered HTML file.
 *
 * Shared by the SSG entry (App.tsx `includedRoutes`) and the service-worker
 * config in vite.config.ts, so the two can never drift apart. Drift is what
 * caused the "/chat serves the homepage" bug: any path without its own static
 * file falls back to index.html — the prerendered HOMEPAGE — which the client
 * then hydrates as a different route (React #418/#422) and swaps out ~1s later.
 */

import localesJson from './content/i18n/locales.json';

/**
 * Localised daily pages (CLAUDE.md I4): every non-default locale gets its own
 * /xx/rashifal, /xx/rashifal/{sign} and /xx/panchang. Derived from the same
 * JSON that drives routing and the sitemap, so a new language is one entry.
 */
const LOCALE_PREFIXES = localesJson.locales.map((l) => l.prefix).filter(Boolean);
export const LOCALIZED_DAILY_PATHS: string[] = LOCALE_PREFIXES.flatMap((p) => [`${p}/rashifal`, `${p}/panchang`]);
export const LOCALIZED_DAILY_PREFIXES: string[] = LOCALE_PREFIXES.map((p) => `${p}/rashifal/`);
/** Topic guides under a site-enabled prefix (/hi/astrology/kundali-matching). */
export const LOCALIZED_TOPIC_PREFIXES: string[] = localesJson.locales.filter((l) => l.site && l.prefix).map((l) => `${l.prefix}/astrology/`);

/**
 * Locales with the FULL public site (`site: true`): home, tools hub, the seven
 * tool pages, about, contact, pricing under their prefix. Same slugs as English —
 * these are tools, not names. Blog, legal and the app stay English-only.
 */
const SITE_PREFIXES = localesJson.locales.filter((l) => l.site).map((l) => l.prefix).filter(Boolean);
export const SITE_PAGE_PATHS: string[] = [
  '/tools', '/about', '/contact', '/pricing', '/astrology',
  '/ai-astrologer', '/free-kundli', '/kundli-matching', '/mangal-dosha-calculator',
  '/nakshatra-calculator', '/dasha-calculator', '/lagna-calculator',
];
export const LOCALIZED_SITE_PATHS: string[] = SITE_PREFIXES.flatMap((p) => [p, ...SITE_PAGE_PATHS.map((path) => `${p}${path}`)]);

/** Public, indexable SEO pages. Also mirrored in scripts/generate-seo-files.mjs (sitemap). */
export const PRERENDER_PATHS: string[] = [
  '/', '/about', '/pricing', '/contact', '/privacy', '/terms', '/blog', '/astrology', '/tools',
  '/ai-astrologer', '/free-kundli', '/kundli-matching', '/mangal-dosha-calculator',
  '/nakshatra-calculator', '/dasha-calculator', '/lagna-calculator', '/panchang', '/rashifal',
  // Reference pages (I3): English-only for now; each nakshatra under /nakshatra/.
  '/nakshatra', '/planet-in-house', '/lagna',
  ...LOCALIZED_DAILY_PATHS,
  ...LOCALIZED_SITE_PATHS,
  // Served by nginx's error_page for any unknown URL. Must render identical
  // markup to the '*' catch-all route, which is what the client renders there.
  '/404',
];

/**
 * Interactive app routes. Not SEO pages (robots.txt disallows them, and they
 * render `noindex`), but they still need their own shell HTML so the browser
 * never paints the homepage at a /chat or /dashboard URL.
 *
 * Each renders a deterministic, auth-independent first paint — ProtectedRoute
 * shows <LoadingPage/> while `isLoaded` is false, which is exactly what the
 * client renders on its first pass, so hydration matches.
 */
export const APP_SHELL_PATHS: string[] = [
  '/chat', '/sign-in', '/sign-up', '/dashboard', '/profile', '/kundali',
  '/predictions', '/reports', '/compatibility', '/dosha', '/remedies', '/payment-status',
];

/** Prefixes whose children are prerendered via getStaticPaths (dynamic slugs). */
export const PRERENDERED_PREFIXES: string[] = ['/blog/', '/astrology/', '/rashifal/', '/nakshatra/', '/planet-in-house/', '/lagna/', ...LOCALIZED_DAILY_PREFIXES, ...LOCALIZED_TOPIC_PREFIXES, '/share/', '/kundali/'];

/**
 * Sentinel id used to prerender a single shell for /share/:shareId.
 *
 * Share ids are unbounded so they can't be enumerated, but every one of them
 * renders the same thing on first paint. nginx serves this shell for any
 * /share/* URL (see the deploy notes), which is what stops a shared link from
 * painting the homepage and then swapping.
 */
export const SHARE_SHELL_ID = '_shell';
